import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { message } from 'antd';
import { Label, LabelEntityType, CreateLabelRequest } from '../../types/labels';
import {
	labelsOnAssetsRestCrudThunks,
	associateAssetLabelsWithEntity,
} from '../../thunks/asset_labels_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { stopDefaultEvents, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { pickRandomLabelColor } from '../../constants/labelConstants';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { AddLabelModal, AddLabelModalLabel } from '../StarLabels/AddLabelModal';
import AddLabelChip, { AddLabelChipVariant } from '../StarLabels/AddLabelChip';

const ASSET_LABELS_DROPDOWN_TC = 'assetLabelsDropdown';
const ASSETS_MIN_CREATE_LENGTH = 3;

type AssetsAddLabelButtonOwnProps = {
	entityType: LabelEntityType;
	entityId: number;
	existingLabels?: AddLabelModalLabel[];
	disabled?: boolean;
	displayClassName?: string;
	variant?: AddLabelChipVariant;
	onChange?: (selectedLabels: AddLabelModalLabel[]) => void;
	onSave?: (selectedLabels: AddLabelModalLabel[]) => void;
	refreshTable?: () => void;
};

type AssetsAddLabelButtonProps = AssetsAddLabelButtonOwnProps & {
	userType: string;
	companyConfig: any;
	currentUser: any;
	catalogue: Label[];
	catalogueFetching: boolean;
	fetchLabels: (search: string) => void;
	createLabel: (label: CreateLabelRequest) => Promise<Label>;
	assignLabels: (entityId: number, labelIds: number[]) => Promise<any>;
};

function AssetsAddLabelButton({
	entityType,
	entityId,
	existingLabels,
	disabled = false,
	displayClassName,
	variant,
	onChange,
	onSave,
	refreshTable,
	userType,
	companyConfig,
	currentUser,
	catalogue,
	catalogueFetching,
	fetchLabels,
	createLabel,
	assignLabels,
}: AssetsAddLabelButtonProps): React.ReactElement {
	const [modalOpen, setModalOpen] = useState(false);

	const roles = nullSafeGetOrElse('roles', currentUser, []);
	const canModifyLabels = useMemo(
		() =>
			isUserAllowedToAccess(userType)(PERMISSION_NAMES.MODIFY_ASSET_LABELS, companyConfig, roles),
		[userType, companyConfig, roles]
	);
	const canApplyLabels = useMemo(
		() =>
			isUserAllowedToAccess(userType)(PERMISSION_NAMES.APPLY_ASSET_LABELS, companyConfig, roles),
		[userType, companyConfig, roles]
	);

	return (
		<div onClick={stopDefaultEvents}>
			<AddLabelChip
				onClick={() => setModalOpen(true)}
				disabled={disabled}
				displayClassName={displayClassName}
				variant={variant}
			/>
			<AddLabelModal
				visible={modalOpen}
				onClose={() => setModalOpen(false)}
				entityId={entityId}
				existingLabels={existingLabels || []}
				canModifyLabels={canModifyLabels}
				canApplyLabels={canApplyLabels}
				catalogue={catalogue}
				catalogueFetching={catalogueFetching}
				fetchLabels={fetchLabels}
				createLabel={createLabel}
				assignLabels={assignLabels}
				pickNewLabelColor={pickRandomLabelColor}
				minCreateLength={ASSETS_MIN_CREATE_LENGTH}
				onAfterAssign={(_id, savedLabels) => {
					message.success('Labels updated');
					if (refreshTable) refreshTable();
					if (onChange) onChange(savedLabels);
					if (onSave) onSave(savedLabels);
				}}
				onAssignError={() => message.error('Failed to update labels')}
			/>
		</div>
	);
}

const mapStateToProps = (state: RootState) => ({
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
	catalogue: getRecordsForTargetCollection(state.asset_labels, ASSET_LABELS_DROPDOWN_TC) || [],
	catalogueFetching: nullSafeGetOrElse(
		`${ASSET_LABELS_DROPDOWN_TC}.fetching`,
		state.asset_labels,
		false
	),
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: AssetsAddLabelButtonOwnProps & { userType: string }
) => ({
	fetchLabels: (search: string) =>
		dispatch(
			labelsOnAssetsRestCrudThunks(ownProps.userType).read(
				{ no_pagination: true, ...(search ? { search } : {}) },
				ASSET_LABELS_DROPDOWN_TC,
				null,
				{ sort_by: 'associatedAssetsCount', order: 'desc' },
				{},
				false
			)
		),
	createLabel: (label: CreateLabelRequest) =>
		dispatch(
			labelsOnAssetsRestCrudThunks(ownProps.userType).create(label, 'id', ownProps.entityType)
		),
	assignLabels: (entityId: number, labelIds: number[]) =>
		dispatch(associateAssetLabelsWithEntity(ownProps.userType)(entityId, labelIds)),
});

// @ts-ignore - HOC composition type inference, same pattern as PartsAddLabelButton
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetsAddLabelButton));
